import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/core/AuthContext';
import { StatPill } from '../../components/trainer/StatPill';
import { WorkoutTemplatesView } from '../../components/trainer/WorkoutTemplatesView';
import { useTrainerExercises } from '../../lib/useTrainerExercises';
import { useTrainerTemplates } from '../../lib/useTrainerTemplates';
import { useTrainerClients } from '../../lib/useTrainerClients';
import { useIsMobile } from '../../lib/useIsMobile';
import { Button } from '../../components/ui';
export function Workouts() {
  const { userData, signOut, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const templateIdFromUrl = searchParams.get('templateId');
  const trainerId = useMemo(
    () => userData?.trainerProfileId || userData?.trainerId || user?.uid,
    [userData, user]
  );
  const isMobile = useIsMobile();
  const {
    exerciseLookup,
    exercises,
  } = useTrainerExercises(trainerId);

  const {
    assignments,
    error: templateError,
    reorderTemplateItems,
    resetTemplateDraft,
    saveTemplate,
    selectedTemplateId,
    setTemplateItems,
    setTemplateName,
    setTemplateNotes,
    startTemplateEdit,
    templateItems,
    templateName,
    templateNotes,
    templates,
  } = useTrainerTemplates(trainerId);

  const { clients } = useTrainerClients(trainerId);
  const linkedClients = useMemo(
    () =>
      clients
        .filter((client) => client.status === 'linked' && client.userId)
        .map((client) => ({
          id: client.userId as string,
          clientDocId: client.id,
          displayName: client.displayName,
          phone: client.phone,
        })),
    [clients]
  );

  useEffect(() => {
    if (!templateIdFromUrl || templates.length === 0) return;
    const templateExists = templates.some((template) => template.id === templateIdFromUrl);
    if (!templateExists) return;

    startTemplateEdit(templateIdFromUrl);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('templateId');
      return next;
    }, { replace: true });
  }, [setSearchParams, startTemplateEdit, templateIdFromUrl, templates]);

  if (!trainerId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Trainer not linked</p>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Your account is missing a trainerId claim. Sign out and re-open your
            secret link, or create the trainer via Cloud Function.
          </p>
          <Button variant="secondary" onClick={signOut} className="mt-4">
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-primary-600 dark:text-primary-400 uppercase tracking-wide font-semibold">
              Workout Management
            </p>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
              Workout Templates
            </h1>
            <p className="text-sm text-gray-600 dark:text-slate-400">Build templates and customize client plans</p>
          </div>
        </div>

        {/* Stats */}
        {!isMobile && (
        <div className="grid sm:grid-cols-4 gap-3">
          <StatPill label="Templates" value={templates.length} />
          <StatPill label="Assignments" value={assignments.length} />
          <StatPill label="Clients" value={linkedClients.length} />
          <StatPill label="Exercises" value={exercises.length} />
        </div>
        )}
        {/* Main Content */}
        <WorkoutTemplatesView
          exercises={exercises}
          exerciseLookup={exerciseLookup}
          templates={templates}
          clients={linkedClients}
          templateName={templateName}
          templateNotes={templateNotes}
          templateItems={templateItems}
          selectedTemplateId={selectedTemplateId}
          error={templateError}
          onSetTemplateName={setTemplateName}
          onSetTemplateNotes={setTemplateNotes}
          onSetTemplateItems={setTemplateItems}
          onReorderTemplateItems={reorderTemplateItems}
          onSaveTemplate={saveTemplate}
          onStartTemplateEdit={startTemplateEdit}
          onResetTemplateDraft={resetTemplateDraft}
        />
      </div>
    </div>
  );
}
